import * as Sentry from "@sentry/nextjs";

/**
 * Client-side upload compression.
 *
 * Shrinks only the genuinely-large photos right before they're uploaded, so a
 * slow mobile connection has far less to push (a 12 MB photo on ~5 Mbps takes
 * ~19s; at 3 MB it's ~5s). Compression must NEVER cost the customer more time
 * than it saves, so it is skipped whenever it can't clearly pay for itself.
 *
 * Deliberately NOT done here:
 *  - HEIC/HEIF conversion. Only Safari decodes HEIC natively; elsewhere it needs
 *    a WASM decoder at ~1-3s per photo (minutes for a large order), and HEIC is
 *    already more efficient than JPEG so converting can make files BIGGER. HEIC
 *    uploads untouched (it's only ~1.6% of real uploads anyway).
 *  - Any MIME type change. Presigned URLs are requested in one batch up front
 *    using each file's name+type, so changing the type would invalidate the
 *    signed content-type. Re-encoding is type-stable (JPEG->JPEG, PNG->PNG),
 *    which is what allows this to run inside each file's own upload task
 *    (overlapping other files' network time) instead of as a blocking pre-pass.
 *
 * This function ALWAYS resolves to a usable File: the compressed one when it
 * clearly wins, otherwise the ORIGINAL, untouched. Disabled/failed/skipped is
 * therefore byte-for-byte identical to not having compression at all.
 */

// Only compress files bigger than this — below it, re-encoding isn't worth it.
const SIZE_THRESHOLD = 3 * 1024 * 1024; // 3 MB
// Longest edge cap. ~42 cm at 300 dpi, comfortably above every print size we
// sell, so this is invisible in print. Never upscales.
const MAX_DIMENSION = 5000;
const JPEG_QUALITY = 0.9;
// On a fast link the upload is quick anyway and compression would be pure added
// latency, so skip it entirely.
const FAST_LINK_MBPS = 10;
// A slow phone must never stall the upload queue.
const COMPRESS_TIMEOUT_MS = 3000;
// Type-stable re-encode only (see header).
const COMPRESSIBLE_TYPES = new Set(["image/jpeg", "image/png"]);

function isEnabled(): boolean {
  // HARD-DISABLED 2026-07-22.
  //
  // Compression shipped to production earlier the same day (3de7869, merged in
  // cb1411e) and is a prime suspect for the mass upload failures we are still
  // chasing: encode() runs createImageBitmap plus a canvas of up to 5000px
  // inside each upload task at concurrency 3, which is on the order of 100MB of
  // RGBA per in-flight photo. A mobile Safari tab killed by memory pressure
  // aborts every in-flight PUT at once with status 0 -- exactly the signature
  // of the "N/N failed" reports.
  //
  // It has not been proven to be the cause. It is being switched off so it can
  // be ruled in or out while the upload diagnostics land, since the upside
  // (slightly smaller uploads) is far smaller than the downside (a whole batch
  // lost). Re-enable by restoring the env check below once the failure rate
  // has been observed with it off.
  //
  //   return process.env.NEXT_PUBLIC_UPLOAD_COMPRESSION !== "false";
  return false;
}

function isFastConnection(): boolean {
  const downlink = (
    navigator as Navigator & { connection?: { downlink?: number } }
  )?.connection?.downlink;
  return typeof downlink === "number" && downlink >= FAST_LINK_MBPS;
}

/** Resolves to null instead of hanging, so one slow file can't block the queue. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(null);
      });
  });
}

async function encode(file: File): Promise<File | null> {
  // Native decode only — no WASM. `imageOrientation: "from-image"` bakes the
  // EXIF orientation into the pixels; without it a plain canvas draw drops the
  // orientation flag and portrait photos print sideways.
  const bitmap = await createImageBitmap(file, {
    imageOrientation: "from-image",
  });
  try {
    const longest = Math.max(bitmap.width, bitmap.height);
    // Only ever shrink.
    const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(
        resolve,
        file.type, // type-stable: same MIME in, same MIME out
        // PNG is lossless and ignores quality; its win comes from the downscale.
        file.type === "image/jpeg" ? JPEG_QUALITY : undefined
      )
    );
    if (!blob) return null;

    return new File([blob], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
  } finally {
    bitmap.close?.();
  }
}

/**
 * Returns a smaller File when compression clearly wins, otherwise the original.
 * Never throws.
 */
export async function compressForUpload(file: File): Promise<File> {
  try {
    if (!isEnabled()) return file;
    if (typeof window === "undefined") return file;
    if (typeof createImageBitmap !== "function") return file;
    if (!COMPRESSIBLE_TYPES.has(file.type)) return file; // HEIC/webp/gif untouched
    if (file.size <= SIZE_THRESHOLD) return file; // already small
    if (isFastConnection()) return file; // wouldn't pay for itself

    const startedAt = Date.now();
    const compressed = await withTimeout(encode(file), COMPRESS_TIMEOUT_MS);
    if (!compressed) return file; // failed or timed out

    // An already-optimised file can come back bigger — keep the original.
    if (compressed.size >= file.size) return file;

    Sentry.addBreadcrumb({
      category: "upload.compress",
      level: "info",
      message: `Compressed ${file.name}`,
      data: {
        type: file.type,
        originalKB: Math.round(file.size / 1024),
        compressedKB: Math.round(compressed.size / 1024),
        ms: Date.now() - startedAt,
      },
    });

    return compressed;
  } catch {
    // Compression must never break an upload.
    return file;
  }
}
